import { Document, Page, Text, View, Image, StyleSheet, pdf } from '@react-pdf/renderer';
import { PLAYER_INFO_FIELDS } from '../../api/waiverApi';
import type { SignatureType } from './waiverSignatureApi';

const BRAND_NAVY = '#201e52';

const styles = StyleSheet.create({
  page: {
    paddingTop: 0,
    paddingBottom: 48,
    paddingHorizontal: 32,
    fontSize: 10,
    fontFamily: 'Helvetica',
    color: '#18181B',
  },
  header: {
    backgroundColor: BRAND_NAVY,
    marginHorizontal: -32,
    paddingHorizontal: 32,
    paddingVertical: 18,
    marginBottom: 20,
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerLogo: {
    width: 28,
    height: 28,
    borderRadius: 4,
    marginRight: 10,
  },
  headerBrand: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: 'bold',
  },
  headerSubtitle: {
    color: '#c9c8e6',
    fontSize: 9,
    marginTop: 1,
  },
  title: {
    fontSize: 18,
    marginBottom: 16,
    fontWeight: 'bold',
    textAlign: 'center',
    color: BRAND_NAVY,
  },
  metaTable: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#bfbfbf',
    borderRadius: 4,
    marginBottom: 18,
  },
  metaRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#bfbfbf',
    borderBottomStyle: 'solid',
  },
  metaLabel: {
    width: '30%',
    padding: 8,
    fontSize: 9,
    color: '#666',
    borderRightWidth: 1,
    borderRightColor: '#bfbfbf',
    borderRightStyle: 'solid',
  },
  metaValue: {
    width: '70%',
    padding: 8,
    fontSize: 10,
    fontWeight: 'bold',
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 8,
    marginTop: 4,
    color: BRAND_NAVY,
  },
  infoTable: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#e4e4e7',
    borderRadius: 4,
    marginBottom: 16,
  },
  infoRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e4e4e7',
    borderBottomStyle: 'solid',
  },
  infoLabel: {
    width: '40%',
    padding: 7,
    fontSize: 9,
    color: '#666',
  },
  infoValue: {
    width: '60%',
    padding: 7,
    fontSize: 9.5,
  },
  consentLine: {
    fontSize: 9.5,
    marginBottom: 4,
    color: '#333',
  },
  signatureBlock: {
    marginBottom: 18,
    padding: 12,
    borderWidth: 1,
    borderColor: '#e4e4e7',
    borderStyle: 'solid',
    borderRadius: 4,
  },
  signatureImage: {
    width: 160,
    height: 60,
    objectFit: 'contain',
    marginBottom: 4,
  },
  signatureTypedText: {
    fontSize: 22,
    fontFamily: 'Helvetica-Oblique',
    color: BRAND_NAVY,
    marginBottom: 4,
  },
  signatureCaption: {
    fontSize: 8,
    color: '#71717A',
  },
  agreementText: {
    fontSize: 9.5,
    lineHeight: 1.5,
    color: '#333',
  },
  footer: {
    position: 'absolute',
    bottom: 18,
    left: 32,
    right: 32,
    fontSize: 7.5,
    color: '#a1a1aa',
    textAlign: 'center',
  },
});

/** Strips HTML tags for a plain-text PDF render of the agreement content. */
function stripHtml(html: string): string {
  return html
    .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

const FIELD_LABELS: Record<string, string> = Object.fromEntries(
  PLAYER_INFO_FIELDS.map((f) => [f.value, f.label])
);

/** Builds the ordered, labeled rows to show under "Player Information" from the captured fieldValues. */
function buildInfoRows(
  playerInfoFields: string[] | null | undefined,
  customFields: string[] | null | undefined,
  fieldValues: Record<string, string> | null | undefined
): { label: string; value: string }[] {
  const values = fieldValues ?? {};
  const rows: { label: string; value: string }[] = [];

  for (const field of playerInfoFields ?? []) {
    if (field === 'MEDICAL_CONDITIONS_DISCLOSURE') continue;
    if (field === 'EMERGENCY_CONTACT') {
      const name = values.EMERGENCY_CONTACT_NAME;
      const phone = values.EMERGENCY_CONTACT_PHONE;
      const relationship = values.EMERGENCY_CONTACT_RELATIONSHIP;
      if (name || phone || relationship) {
        rows.push({
          label: 'Emergency Contact',
          value: [name, phone, relationship].filter(Boolean).join(' · '),
        });
      }
      continue;
    }
    if (values[field]) {
      rows.push({ label: FIELD_LABELS[field] ?? field, value: values[field] });
    }
  }

  for (const customField of customFields ?? []) {
    if (values[customField]) {
      rows.push({ label: customField, value: values[customField] });
    }
  }

  return rows;
}

interface SignedWaiverPdfProps {
  waiverName: string;
  tournamentName: string;
  teamName: string;
  signedBy: string;
  signedOn: string;
  agreementContent: string;
  signatureType?: SignatureType | null;
  signatureImageUrl?: string | null;
  agreeCheckboxLabel?: string | null;
  agreedToTerms?: boolean;
  signerIsMinor?: boolean;
  parentGuardianName?: string | null;
  playerInfoFields?: string[] | null;
  customFields?: string[] | null;
  fieldValues?: Record<string, string> | null;
}

function SignedWaiverPdf({
  waiverName,
  tournamentName,
  teamName,
  signedBy,
  signedOn,
  agreementContent,
  signatureType,
  signatureImageUrl,
  agreeCheckboxLabel,
  agreedToTerms,
  signerIsMinor,
  parentGuardianName,
  playerInfoFields,
  customFields,
  fieldValues,
}: SignedWaiverPdfProps) {
  const infoRows = buildInfoRows(playerInfoFields, customFields, fieldValues);
  const logoUrl = typeof window !== 'undefined' ? `${window.location.origin}/favicon.png` : undefined;
  const isDrawn = signatureType === 'DRAWN' && !!signatureImageUrl;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header} fixed>
          {logoUrl && <Image src={logoUrl} style={styles.headerLogo} />}
          <View>
            <Text style={styles.headerBrand}>CricCenter</Text>
            <Text style={styles.headerSubtitle}>Signed Waiver Record</Text>
          </View>
        </View>

        <Text style={styles.title}>{waiverName}</Text>

        <View style={styles.metaTable}>
          <View style={styles.metaRow}>
            <Text style={styles.metaLabel}>Tournament</Text>
            <Text style={styles.metaValue}>{tournamentName}</Text>
          </View>
          <View style={styles.metaRow}>
            <Text style={styles.metaLabel}>Team</Text>
            <Text style={styles.metaValue}>{teamName}</Text>
          </View>
          <View style={styles.metaRow}>
            <Text style={styles.metaLabel}>Signed by</Text>
            <Text style={styles.metaValue}>{isDrawn ? 'Drawn signature (below)' : signedBy}</Text>
          </View>
          <View style={[styles.metaRow, { borderBottomWidth: 0 }]}>
            <Text style={styles.metaLabel}>Signed on</Text>
            <Text style={styles.metaValue}>{signedOn}</Text>
          </View>
        </View>

        {infoRows.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>Player Information</Text>
            <View style={styles.infoTable}>
              {infoRows.map((row, idx) => (
                <View
                  key={row.label}
                  style={idx === infoRows.length - 1 ? [styles.infoRow, { borderBottomWidth: 0 }] : styles.infoRow}
                >
                  <Text style={styles.infoLabel}>{row.label}</Text>
                  <Text style={styles.infoValue}>{row.value}</Text>
                </View>
              ))}
            </View>
          </>
        )}

        {(agreedToTerms || (signerIsMinor && parentGuardianName)) && (
          <View style={{ marginBottom: 12 }}>
            {agreedToTerms && (
              <Text style={styles.consentLine}>
                ✓ {agreeCheckboxLabel || 'I have read and agree to this waiver, and I agree to sign it electronically.'}
              </Text>
            )}
            {signerIsMinor && parentGuardianName && (
              <Text style={styles.consentLine}>Parent/Guardian: {parentGuardianName} (signer is a minor)</Text>
            )}
          </View>
        )}

        <Text style={styles.sectionTitle}>Signature</Text>
        <View style={styles.signatureBlock}>
          {isDrawn ? (
            <Image src={signatureImageUrl as string} style={styles.signatureImage} />
          ) : (
            <Text style={styles.signatureTypedText}>{signedBy}</Text>
          )}
          <Text style={styles.signatureCaption}>
            Signed electronically on {signedOn} via CricCenter.
          </Text>
        </View>

        <Text style={styles.sectionTitle}>Waiver Agreement</Text>
        <Text style={styles.agreementText}>{stripHtml(agreementContent)}</Text>

        <Text
          style={styles.footer}
          render={({ pageNumber, totalPages }) =>
            `CricCenter · Generated ${new Date().toLocaleDateString()} · Page ${pageNumber} of ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
}

export async function exportSignedWaiverToPDF(props: SignedWaiverPdfProps): Promise<void> {
  const doc = <SignedWaiverPdf {...props} />;
  const asPdf = pdf(doc);
  const blob = await asPdf.toBlob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${props.waiverName.replace(/\s+/g, '_')}_Signed_${new Date().toISOString().split('T')[0]}.pdf`;
  link.click();
  URL.revokeObjectURL(url);
}
